import { useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { api } from "../api";

export function Login() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [debugLink, setDebugLink] = useState<string | null>(null);
  const [delivered, setDelivered] = useState(true);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.requestMagicLink(email.trim());
      setSent(true);
      setDelivered(res.delivered);
      setDebugLink(res.debugLink ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to send link");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md mx-auto pt-8 text-center"
      >
        <h1 className="font-display text-3xl mb-1">check your inbox</h1>
        <p className="text-ink-400 text-sm mb-6">
          we sent a sign-in link to <span className="text-ink-200 font-mono">{email}</span>. it expires in 15 minutes.
        </p>
        {debugLink && (
          <div className="glass rounded-2xl p-4 text-left">
            <div className="text-xs uppercase tracking-wider text-amber-300 mb-2">
              {delivered ? "local dev — open this link directly" : "email wasn't delivered"}
            </div>
            <a
              href={debugLink}
              className="text-accent font-mono text-xs break-all hover:underline"
            >
              {debugLink}
            </a>
          </div>
        )}
        <button
          onClick={() => {
            setSent(false);
            setDebugLink(null);
            setError(null);
          }}
          className="mt-6 text-sm text-ink-400 hover:text-ink-200"
        >
          use a different email
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto pt-8"
    >
      <h1 className="font-display text-3xl mb-1">sign in</h1>
      <p className="text-ink-400 text-sm mb-6">
        enter your email and we'll send you a one-time sign-in link. no password.
      </p>
      <form onSubmit={onSubmit} className="glass rounded-2xl p-6 flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs uppercase tracking-wider text-ink-400">email</span>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            autoComplete="email"
            required
            autoFocus
            className="px-3 py-2 rounded-lg glass-soft focus:ring-2 focus:ring-accent/60"
          />
        </label>
        {error && <p className="text-red-400 text-sm">{error}</p>}
        <button
          type="submit"
          disabled={busy || !email.trim()}
          className="px-4 py-2 rounded-xl bg-accent text-ink-950 font-medium hover:opacity-90 disabled:opacity-40"
        >
          {busy ? "sending…" : "send me a link"}
        </button>
      </form>
      <p className="text-xs text-ink-400 text-center mt-4">
        guest stats and rounds will move to your account.
      </p>
    </motion.div>
  );
}
